import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Heart, Copy, ArrowRight, Sparkles, Link as LinkIcon, Share2 } from "lucide-react";

export default function Onboarding() {
  const { user, couple, refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(couple ? "done" : null); // null|create|join|done
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLink, setMagicLink] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);

  const createCouple = async () => {
    setLoading(true);
    try {
      await api.post("/couple/create");
      await refresh();
      setMode("created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const joinCouple = async () => {
    setLoading(true);
    try {
      await api.post("/couple/join", { invite_code: code });
      await refresh();
      toast.success("Couple Space verbunden 💛");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (couple?.invite_code) {
      navigator.clipboard.writeText(couple.invite_code);
      toast.success("Code kopiert");
    }
  };

  const generateMagicLink = async () => {
    setMagicLoading(true);
    try {
      const { data } = await api.post("/couple/invite-link");
      const url = `${window.location.origin}/join/${data.token}`;
      setMagicLink(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMagicLoading(false);
    }
  };

  const copyMagicLink = () => {
    if (!magicLink) return;
    navigator.clipboard.writeText(magicLink);
    toast.success("Einladungs-Link kopiert");
  };

  const shareMagicLink = async () => {
    if (!magicLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Wisha — Couple Space",
          text: `${user?.name || "Jemand"} möchte dich auf Wisha verbinden 💛`,
          url: magicLink,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyMagicLink();
    }
  };

  return (
    <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
      <div className="max-w-2xl w-full my-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-4">
            <Sparkles size={12} /> Schritt 2 von 2
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink">
            Hi {user?.name},<br />
            <span className="italic text-terracotta">jetzt zu zweit.</span>
          </h1>
          <p className="text-text_secondary mt-4 max-w-md mx-auto">
            Wisha lebt von gemeinsamem Erleben. Verbindet euren Account mit eurer Partner:in oder beginnt zunächst alleine.
          </p>
        </div>

        {!mode && (
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              onClick={() => setMode("create")}
              className="bg-white border border-border rounded-3xl p-8 text-left hover:border-ink/30 transition-colors group"
              data-testid="onboarding-create-button"
            >
              <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center mb-6">
                <Heart size={20} strokeWidth={1.6} className="text-terracotta" />
              </div>
              <h3 className="font-serif text-2xl text-ink mb-2">Couple Space starten</h3>
              <p className="text-text_secondary text-sm">Ich erstelle den Space und lade meine Partner:in mit einem Code ein.</p>
              <div className="mt-6 flex items-center text-ink text-sm font-medium">
                Starten <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            <button
              onClick={() => setMode("join")}
              className="bg-white border border-border rounded-3xl p-8 text-left hover:border-ink/30 transition-colors group"
              data-testid="onboarding-join-button"
            >
              <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center mb-6">
                <Sparkles size={20} strokeWidth={1.6} className="text-sage" />
              </div>
              <h3 className="font-serif text-2xl text-ink mb-2">Mit Code beitreten</h3>
              <p className="text-text_secondary text-sm">Meine Partner:in hat schon einen Code für mich.</p>
              <div className="mt-6 flex items-center text-ink text-sm font-medium">
                Beitreten <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>
        )}

        {mode === "create" && !couple && (
          <div className="bg-white border border-border rounded-3xl p-8 sm:p-12 text-center">
            <h3 className="font-serif text-2xl text-ink mb-3">Bereit?</h3>
            <p className="text-text_secondary mb-8">Wir generieren einen einmaligen Code, mit dem deine Partner:in beitreten kann.</p>
            <Button onClick={createCouple} disabled={loading} className="rounded-full bg-ink text-alabaster hover:bg-ink/90 px-8 py-6" data-testid="create-couple-confirm">
              {loading ? "Erstelle Space …" : "Couple Space erstellen"}
            </Button>
          </div>
        )}

        {(mode === "created" || (couple && couple.members?.length === 1)) && (
          <div className="bg-white border border-border rounded-3xl p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-bone mx-auto flex items-center justify-center mb-6">
              <Heart size={24} className="text-terracotta" />
            </div>
            <h3 className="font-serif text-2xl text-ink mb-2">Euer Code ist bereit</h3>
            <p className="text-text_secondary mb-6">Teilt diesen Code mit eurer Partner:in:</p>
            <div className="inline-flex items-center gap-3 bg-bone rounded-full px-6 py-4">
              <span className="font-mono text-3xl tracking-[0.4em] text-ink" data-testid="invite-code-display">{couple?.invite_code}</span>
              <button onClick={copyCode} className="p-2 rounded-full hover:bg-white" data-testid="copy-invite-code">
                <Copy size={16} />
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-border">
              <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-text_secondary mb-4">
                <LinkIcon size={12} /> Oder per Magic-Link
              </div>
              {!magicLink ? (
                <Button
                  onClick={generateMagicLink}
                  disabled={magicLoading}
                  variant="outline"
                  className="rounded-full px-6"
                  data-testid="generate-magic-link"
                >
                  {magicLoading ? "Generiere …" : "Magic-Link erstellen"}
                </Button>
              ) : (
                <div className="space-y-3" data-testid="magic-link-section">
                  <div className="flex items-center gap-2 bg-bone rounded-2xl px-4 py-3 text-left">
                    <span className="font-mono text-xs text-text_secondary truncate flex-1" data-testid="magic-link-url">{magicLink}</span>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={copyMagicLink} variant="outline" className="rounded-full" data-testid="copy-magic-link">
                      <Copy size={14} className="mr-2" /> Kopieren
                    </Button>
                    <Button onClick={shareMagicLink} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="share-magic-link">
                      <Share2 size={14} className="mr-2" /> Teilen
                    </Button>
                  </div>
                  <p className="text-[11px] text-text_secondary">14 Tage gültig</p>
                </div>
              )}
            </div>

            <div className="mt-10">
              <Button onClick={() => navigate("/dashboard")} variant="ghost" className="rounded-full" data-testid="onboarding-skip">
                Erstmal alleine starten →
              </Button>
            </div>
          </div>
        )}

        {mode === "join" && (
          <div className="bg-white border border-border rounded-3xl p-8 sm:p-12">
            <h3 className="font-serif text-2xl text-ink mb-3">Code eingeben</h3>
            <p className="text-text_secondary mb-8">Gib den 6-stelligen Code ein, den du erhalten hast.</p>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="A1B2C3"
              className="text-center font-mono text-3xl tracking-[0.4em] py-8 rounded-2xl bg-bone border-border"
              data-testid="invite-code-input"
            />
            <div className="flex gap-2 mt-6">
              <Button variant="ghost" onClick={() => setMode(null)} className="rounded-full">Zurück</Button>
              <Button onClick={joinCouple} disabled={loading || code.length !== 6} className="flex-1 rounded-full bg-ink text-alabaster hover:bg-ink/90 py-6" data-testid="join-couple-confirm">
                {loading ? "Beitreten …" : "Beitreten"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
